"use client";

import { useState } from "react";
import { useInvisibleRecaptcha } from "@/lib/useInvisibleRecaptcha";
import { isValidEmail, isValidPhone, sanitizePhone } from "@/lib/validators";

interface GroupsBookingFormProps {
  successMessage: string;
  failureMessage: string;
}

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  message: string;
  honeypot: string;
}

const INITIAL: FormState = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  message: "",
  honeypot: "",
};

type Result = "" | "success" | "error";

export default function GroupsBookingForm({
  successMessage,
  failureMessage,
}: GroupsBookingFormProps) {
  const [form, setForm] = useState<FormState>(INITIAL);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<Result>("");
  const { containerRef: recaptchaRef, getToken } = useInvisibleRecaptcha();

  const update = <K extends keyof FormState>(field: K, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));
  const touch = (field: string) =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  const errors = {
    firstName: !form.firstName.trim() ? "First name is required" : "",
    lastName: !form.lastName.trim() ? "Last name is required" : "",
    email: !form.email.trim()
      ? "Email address is required"
      : !isValidEmail(form.email)
        ? "Please enter a valid email address"
        : "",
    phone: !form.phone.trim()
      ? "Phone number is required"
      : !isValidPhone(form.phone)
        ? "Please enter a valid phone number"
        : "",
    message: !form.message.trim() ? "Message is required" : "",
  };

  const isValid = Object.values(errors).every((e) => !e);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Reveal any untouched errors on first submit attempt.
    setTouched({
      firstName: true,
      lastName: true,
      email: true,
      phone: true,
      message: true,
    });
    if (!isValid || submitting) return;

    setSubmitting(true);
    setResult("");

    try {
      // Runs the invisible challenge. Resolves "" when reCAPTCHA is unavailable
      // (script blocked, no site key) — the route handler decides what that
      // means, so the rule stays in one place for every form.
      const recaptchaToken = await getToken();

      // Same-origin, so this deliberately uses fetch rather than the shared
      // axios client — no CRM base URL or bearer token belongs on this call.
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          brandId: process.env.NEXT_PUBLIC_BRAND_ID_GROUPS,
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          phone: form.phone.trim(),
          email: form.email.trim(),
          message: form.message.trim(),
          honeypot: form.honeypot,
          recaptchaToken,
        }),
      });

      if (!response.ok) throw new Error("Contact submission failed");

      setResult("success");
      setForm(INITIAL);
      setTouched({});
    } catch {
      setResult("error");
    } finally {
      setSubmitting(false);
    }
  };

  const fieldError = (key: keyof typeof errors) =>
    touched[key] && errors[key] ? (
      <small
        className="d-block mt-2"
        style={{ color: "var(--error-color, #e54848)" }}
      >
        {errors[key]}
      </small>
    ) : null;

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      autoComplete="off"
      className="booking-form2"
    >
      <div className="hero-wrap">
        <div className="title-area mb-40">
          <span className="sub-title2 style1 mb-15">LET&apos;S CONNECT</span>
          <h2 className="sec-title">Start Planning Your Group</h2>
        </div>

        {/* Honeypot — visually hidden, only filled by bots */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            left: "-10000px",
            top: "auto",
            width: 1,
            height: 1,
            overflow: "hidden",
          }}
        >
          <label>
            Leave this field empty
            <input
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={form.honeypot}
              onChange={(e) => update("honeypot", e.target.value)}
            />
          </label>
        </div>

        <div className="row gx-24 align-items-start justify-content-between">
          <div className="form-group col-md-6">
            <div className="form-item">
              <label htmlFor="g-firstname">First Name</label>
              <input
                id="g-firstname"
                type="text"
                className="form-control"
                placeholder="First Name"
                value={form.firstName}
                onBlur={() => touch("firstName")}
                onChange={(e) => update("firstName", e.target.value)}
                disabled={submitting}
                required
              />
              <i className="fa-solid fa-user"></i>
            </div>
            {fieldError("firstName")}
          </div>

          <div className="form-group col-md-6">
            <div className="form-item">
              <label htmlFor="g-lastname">Last Name</label>
              <input
                id="g-lastname"
                type="text"
                className="form-control"
                placeholder="Last Name"
                value={form.lastName}
                onBlur={() => touch("lastName")}
                onChange={(e) => update("lastName", e.target.value)}
                disabled={submitting}
                required
              />
              <i className="fa-solid fa-user"></i>
            </div>
            {fieldError("lastName")}
          </div>

          <div className="form-group col-md-6">
            <div className="form-item">
              <label htmlFor="g-email">Email Address</label>
              <input
                id="g-email"
                type="email"
                className="form-control"
                placeholder="email@domain.com"
                value={form.email}
                onBlur={() => touch("email")}
                onChange={(e) => update("email", e.target.value)}
                disabled={submitting}
                required
              />
              <i className="fa-solid fa-envelope"></i>
            </div>
            {fieldError("email")}
          </div>

          <div className="form-group col-md-6">
            <div className="form-item">
              <label htmlFor="g-phone">Phone Number</label>
              <input
                id="g-phone"
                type="tel"
                className="form-control"
                placeholder="+1 (555) 000-0000"
                value={form.phone}
                onBlur={() => touch("phone")}
                onChange={(e) => update("phone", sanitizePhone(e.target.value))}
                disabled={submitting}
                required
              />
              <i className="fa-solid fa-phone-alt"></i>
            </div>
            {fieldError("phone")}
          </div>

          <div className="form-group col-12">
            <div className="form-item">
              <label htmlFor="g-message">Group Details &amp; Message</label>
              <textarea
                id="g-message"
                className="form-control"
                placeholder="Tell us about your group (Destination, Size, Dates, Special Interests)..."
                style={{ height: 120, paddingTop: 15 }}
                value={form.message}
                onBlur={() => touch("message")}
                onChange={(e) => update("message", e.target.value)}
                disabled={submitting}
                required
              />
            </div>
            {fieldError("message")}
          </div>

          {/* Invisible reCAPTCHA mounts here — renders no UI of its own beyond
              the floating badge, and only surfaces a challenge when Google
              considers the visitor suspicious. */}
          <div ref={recaptchaRef} />

          <div className="btn-form">
            <button
              type="submit"
              className="th-btn style1"
              disabled={submitting}
              aria-busy={submitting}
            >
              {submitting ? (
                <span className="d-inline-flex align-items-center gap-2">
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  />
                  SENDING…
                </span>
              ) : (
                "SUBMIT INQUIRY"
              )}
            </button>
          </div>
        </div>

        {result === "success" && (
          <div
            className="form-messages success mt-3"
            role="status"
            aria-live="polite"
          >
            <i className="fa-solid fa-circle-check me-2"></i>
            {successMessage}
          </div>
        )}
        {result === "error" && (
          <div
            className="form-messages error mt-3"
            role="alert"
            aria-live="assertive"
          >
            <i className="fa-solid fa-circle-exclamation me-2"></i>
            {failureMessage}{" "}
            <button
              type="button"
              className="btn-link p-0 ms-1"
              style={{
                background: "none",
                border: 0,
                color: "inherit",
                textDecoration: "underline",
                cursor: "pointer",
              }}
              onClick={() => setResult("")}
            >
              Try again
            </button>
          </div>
        )}
      </div>
    </form>
  );
}
